// Sidebar low-balance prompt. Pure so it can be unit-tested.
import { formatCredit, needsCredit, type WalletSummary } from "@/lib/wallet";

export interface LowBalancePrompt {
  /** Distinguishes "low" from "empty" so dismissing one doesn't hide the other. */
  key: "low" | "empty";
  title: string;
  body: string;
  cta: string;
  /** True when the balance is used up and API calls are refused. */
  urgent: boolean;
}

/**
 * Prompt to add credit when the wallet is below the user's alert threshold
 * or empty. Never shown to admins (they are not charged) or while loading.
 */
export function getLowBalancePrompt(opts: {
  isAdmin: boolean;
  wallet: Pick<WalletSummary, "balance" | "lowBalance" | "currency"> | null | undefined;
}): LowBalancePrompt | null {
  const w = opts.wallet;
  if (opts.isAdmin || !w || !needsCredit(w)) return null;
  if (w.balance <= 0) {
    return {
      key: "empty",
      urgent: true,
      title: "You're out of credit",
      body: "API requests are paused until you add credit.",
      cta: "Add credit",
    };
  }
  return {
    key: "low",
    urgent: false,
    title: "Your credit is running low",
    body: `${formatCredit(w.balance, w.currency)} left. Add credit to avoid interruptions.`,
    cta: "Add credit",
  };
}
